import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  clampInspectorWidth,
  INSPECTOR_DEFAULT_WIDTH_PX,
} from '../features/inspector/inspectorLayout';
import { storageService } from '../services/storageService';

interface DragState {
  startX: number;
  startWidth: number;
}

/**
 * Owns inspector panel width: load/persist via storageService and drag-to-resize.
 * The panel is always visible — there is no collapse mode.
 */
export function useResizableInspectorWidth(): {
  width: number;
  isResizing: boolean;
  onResizePointerDown: (event: React.PointerEvent<HTMLElement>) => void;
} {
  const [width, setWidth] = useState<number>(() => {
    const stored = storageService.getInspectorWidth();
    if (stored === null) {
      return INSPECTOR_DEFAULT_WIDTH_PX;
    }
    return clampInspectorWidth(stored);
  });
  const [isResizing, setIsResizing] = useState(false);
  const dragRef = useRef<DragState | null>(null);
  const widthRef = useRef(width);

  useEffect(() => {
    widthRef.current = width;
  }, [width]);

  const onResizePointerDown = useCallback((event: React.PointerEvent<HTMLElement>) => {
    event.preventDefault();
    const target = event.currentTarget;
    if (typeof target.setPointerCapture === 'function') {
      target.setPointerCapture(event.pointerId);
    }

    dragRef.current = { startX: event.clientX, startWidth: widthRef.current };
    setIsResizing(true);

    const previousUserSelect = document.body.style.userSelect;
    const previousCursor = document.body.style.cursor;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';

    const onMove = (moveEvent: MouseEvent) => {
      const drag = dragRef.current;
      if (!drag) {
        return;
      }
      // Dragging the left edge leftward widens the panel.
      const next = clampInspectorWidth(drag.startWidth + (drag.startX - moveEvent.clientX));
      widthRef.current = next;
      setWidth(next);
    };

    const endDrag = () => {
      dragRef.current = null;
      setIsResizing(false);
      storageService.saveInspectorWidth(clampInspectorWidth(widthRef.current));
      document.body.style.userSelect = previousUserSelect;
      document.body.style.cursor = previousCursor;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('pointerup', endDrag);
      window.removeEventListener('mouseup', endDrag);
      window.removeEventListener('pointercancel', endDrag);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('mouseup', endDrag);
    window.addEventListener('pointercancel', endDrag);
  }, []);

  return { width, isResizing, onResizePointerDown };
}
